/**
 * Curriculum v2 builder: world plans → paths (with chapters, side branches, finale), mini units for the
 * ten activity types, and the 4-level playground (docs/CURRICULUM-V2.md §A.3, §D, §E.4).
 * Mini-unit ids: `mu-<drawingId>` (drawing activities, = v1 ids so progress carries over),
 * `mu-<remixId>`, `mu-<colourId>`, `mu-<challengeId>`, `mu-<observeId>`, `mu-<crossId>`; playground `mu-pg-<id>`.
 */
import { t, tf, type L } from '../../text';
import { buildLessonMiniUnit, buildPlaygroundMiniUnit, lessonMiniUnitId } from '../lessons';
import { playgroundItemId, PREVIEW_DRAWINGS } from '../playground';
import { colourStepIndex } from '../art/compile';
import type {
  ChallengeDef,
  CrossoverDef,
  Drawing,
  MiniUnit,
  MiniUnitItem,
  ObserveDef,
  PathConfig,
  PathId,
  PathUnit,
  PathUnitItem,
  PictureLightKind,
  PlaygroundCategory,
  PlaygroundConfig,
  PlaygroundItem,
  SkillId,
  StoryGuide,
} from '../model';
import { SKILL_XP_PER_LESSON } from '../../constants';
import { checklistFor, scaffoldForLevel } from './author';
import { lightPlace, shadowSide } from './picture';
import { COIN_BY_ID, type PathActivity, type PathPlanV2, type WorldV2 } from './types';

const xpOf = (skills: SkillId[]) => Object.fromEntries(skills.map((s) => [s, SKILL_XP_PER_LESSON]));

/** Playground category of each world (docs §D.1.2): the warm-up gets the new "Basics" shelf. */
export const WORLD_PG_CATEGORY: Record<PathId, string> = { intro: 'basics', animals: 'animals', nature: 'nature', characters: 'characters', anime: 'manga', cuteObjects: 'cute' };

/** Picture tier shelf (Playground \"Tranh\" filter). */
export const PICTURES_CATEGORY = 'pictures';

export const PLAYGROUND_CATEGORIES_V2: PlaygroundCategory[] = [
  { id: 'quick', title: t('Quick doodles', 'Vẽ nhanh'), icon: 'bolt' },
  { id: 'basics', title: t('Basics', 'Cơ bản'), icon: 'pencil' },
  { id: 'animals', title: t('Animals', 'Động vật'), icon: 'paw' },
  { id: 'nature', title: t('Nature', 'Thiên nhiên'), icon: 'leaf' },
  { id: 'characters', title: t('Characters', 'Nhân vật'), icon: 'smile' },
  { id: 'manga', title: t('Manga style', 'Truyện tranh'), icon: 'sparkle' },
  { id: 'cute', title: t('Cute objects', 'Đồ dễ thương'), icon: 'cupcake' },
  { id: PICTURES_CATEGORY, title: t('Pictures', 'Tranh'), icon: 'gallery' },
];

const SEASONS_V2: Record<string, PlaygroundItem['season']> = {
  'doodle-ghost': 'autumn',
  'doodle-pumpkin': 'autumn',
  'doodle-snowman': 'winter',
  'doodle-flower': 'spring',
  'nature-beach-sunset': 'summer',
  'cute-gift-box': 'holiday',
  'nature-doodle-snowflake': 'winter',
  'nature-apple-basket': 'autumn',
};

const CHALLENGE_TITLES: Record<1 | 2 | 3 | 4, L> = {
  1: t('Remember & draw', 'Nhớ và vẽ'),
  2: t('Quick memory', 'Nhớ nhanh'),
  3: t('No guides', 'Không nét dựng'),
  4: t('Confident artist', 'Hoạ sĩ tự tin'),
};

export interface WorldV2Input {
  id: PathId;
  v2: WorldV2;
}

export interface BuiltCurriculumV2 {
  paths: PathConfig[];
  miniUnits: MiniUnit[];
  playground: PlaygroundConfig;
  levelChallenges: ChallengeDef[];
  observe: ObserveDef[];
  crossovers: CrossoverDef[];
}

/* -------------------------------------- mini units -------------------------------------- */

function storyBeat(id: string, plan: PathPlanV2, level: number, phase: 'intro' | 'outro' | 'finale'): MiniUnitItem {
  const lv = plan.levels[Math.min(3, Math.max(0, level - 1))]!;
  const line = phase === 'intro' ? lv.chapter.intro : phase === 'outro' ? lv.chapter.outro : plan.story.finale.text;
  return { id, type: 'storyBeat', world: plan.id, chapter: level, speaker: plan.story.guide as StoryGuide, title: phase === 'finale' ? plan.story.title : lv.chapter.title, line, phase };
}

const tail = (id: (s: string) => string, reflect: SkillId, question: L, variant?: 'small' | 'key' | 'masterpiece' | 'badge', frame?: string): MiniUnitItem[] => [
  { id: id('feedback'), type: 'animatedFeedback', reflect, question },
  { id: id('journal'), type: 'uploadToJournal', ...(frame ? { frame } : {}) },
  { id: id('celebrate'), type: 'celebration', ...(variant ? { variant } : {}) },
];

const FINISH = t('Finish your drawing, then compare it with mine', 'Hoàn thành bức vẽ, rồi so sánh với hình mẫu nhé');
const PROUD = t('Look at your drawing. What are you most proud of?', 'Nhìn bức vẽ của con nhé. Con tự hào nhất điều gì?');

function doodleUnit(d: Drawing, level: number): MiniUnit {
  const id = (s: string) => `${d.id}:${s}`;
  const items: MiniUnitItem[] = [];
  if (level === 1) items.push({ id: id('warmup'), type: 'animation', anim: 'pencilWarmup', text: t('Wiggle your fingers and warm up your pencil', 'Ngọ nguậy ngón tay và khởi động bút chì nào') });
  items.push({ id: id('video'), type: 'video', drawingId: d.id, scaffold: d.scaffold ?? scaffoldForLevel(level) });
  items.push({ id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'segmentation' });
  items.push(...tail(id, d.skills[0]!, t('Quick and fun! What did you like?', 'Nhanh mà vui! Con thích điều gì?'), 'small'));
  return { id: lessonMiniUnitId(d.id), title: d.title, drawingId: d.id, skillXp: xpOf(d.skills), items, noDrawingFallback: 'completionAnimation' };
}

function coreUnit(d: Drawing, level: number, n: number, siblings: Drawing[], first: boolean): MiniUnit {
  const mu = buildLessonMiniUnit(d, { n, siblings, first });
  const scaffold = d.scaffold ?? scaffoldForLevel(level);
  const items = mu.items.map((it) => (it.type === 'video' ? { ...it, scaffold } : it));
  if (level === 1 && d.art) {
    // L1 cores may print a "trace sheet" (ghost lines) before drawing on paper.
    const at = items.findIndex((i) => i.type === 'canvas');
    items.splice(at, 0, { id: `${d.id}:print`, type: 'printSheet', drawingId: d.id, mode: 'trace', text: t('Want to trace first? Print a practice sheet', 'Muốn đồ trước không? In một trang tập đồ nhé') });
  }
  return { ...mu, items };
}

function remixUnit(a: PathActivity, cards: Drawing[], level: number): MiniUnit {
  const id = (s: string) => `${a.id}:${s}`;
  const pick = id('pick');
  const first = cards[0]!;
  const base = first.remix?.base ?? first.id;
  const items: MiniUnitItem[] = [
    { id: pick, type: 'remixPicker', base, cards: cards.map((c) => ({ drawingId: c.id, label: c.remix?.card ?? c.title })), text: t('Flip the cards and pick your remix!', 'Lật thẻ và chọn bản biến hoá của con!') },
    { id: id('video'), type: 'video', drawingId: first.id, fromChoice: pick, scaffold: first.scaffold ?? scaffoldForLevel(level) },
    { id: id('canvas'), type: 'canvas', drawingId: first.id, fromChoice: pick, text: FINISH },
    { id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'details' },
    ...tail(id, 'creativity', t('What did you change? Tell me about your remix!', 'Con đã đổi điều gì? Kể cho cô nghe về bản biến hoá nhé!')),
  ];
  const skills = [...new Set<SkillId>(['creativity', ...first.skills])];
  return { id: lessonMiniUnitId(a.id), title: a.title ?? first.title, drawingId: first.id, skillXp: xpOf(skills), items, noDrawingFallback: 'completionAnimation' };
}

const COLOUR_SKILL: Record<number, SkillId> = { 1: 'pressure', 2: 'shading', 3: 'texture', 4: 'shading' };

function colouringUnit(a: PathActivity, d: Drawing, level: number): MiniUnit {
  const id = (s: string) => `${a.id}:${s}`;
  const items: MiniUnitItem[] = [];
  if (level >= 2 || a.coinPiece === 'shading') items.push({ id: id('light'), type: 'animation', anim: 'lightSource', text: t('Light comes from one side; shadows hide on the other', 'Ánh sáng chiếu từ một phía; bóng tối nấp ở phía kia') });
  const outline = Math.max(0, (d.art ? colourStepIndex(d) : d.steps.length) - 1);
  items.push({ id: id('canvas'), type: 'canvas', drawingId: d.id, step: outline, text: tf('Use the drawing you made in "{title}" (or redraw the outline)', 'Dùng tranh con vẽ ở bài "{title}" (hoặc vẽ nhanh lại nét viền)', { title: d.title }) });
  items.push({ id: id('print'), type: 'printSheet', drawingId: d.id, mode: 'colour', text: t('No drawing at hand? Print a colouring page', 'Chưa có tranh? In một trang tô màu nhé') });
  items.push({ id: id('coach'), type: 'colourCoach', drawingId: d.id, level: Math.min(4, Math.max(1, level)) as 1 | 2 | 3 | 4 });
  items.push({ id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'colour', drawingId: d.id });
  items.push(...tail(id, COLOUR_SKILL[level] ?? 'shading', t('Which colour are you proudest of?', 'Con tự hào nhất với màu nào?')));
  const skills: SkillId[] = [...new Set<SkillId>([COLOUR_SKILL[level] ?? 'shading', 'pressure'])];
  return { id: lessonMiniUnitId(a.id), title: a.title ?? tf('Colour the {title}', 'Tô màu {title}', { title: d.title }), drawingId: d.id, skillXp: xpOf(skills), items, noDrawingFallback: 'completionAnimation' };
}

function sceneUnit(d: Drawing, plan: PathPlanV2, level: number, masterpiece: boolean): MiniUnit {
  const id = (s: string) => `${d.id}:${s}`;
  const items: MiniUnitItem[] = [storyBeat(id('story'), plan, level, 'outro')];
  items.push({ id: id('stage'), type: 'sceneStage', drawingId: d.id, ...(masterpiece ? { arrange: true } : {}), text: masterpiece ? t('Choose your layout: far, middle and near', 'Chọn bố cục của con: xa, giữa và gần') : t('Where does everything go? Look at the boxes and the horizon', 'Mọi thứ đặt ở đâu? Nhìn các khung và đường chân trời nhé') });
  items.push({ id: id('video'), type: 'video', drawingId: d.id, scaffold: d.scaffold ?? (masterpiece ? 'none' : scaffoldForLevel(level)), ...(masterpiece ? { resumable: true } : {}) });
  items.push({ id: id('canvas'), type: 'canvas', drawingId: d.id, text: FINISH });
  if (masterpiece) items.push({ id: id('coach'), type: 'colourCoach', drawingId: d.id, level: 4 });
  items.push({ id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: masterpiece ? 'details' : 'placement' });
  items.push(
    ...tail(
      id,
      masterpiece ? 'shading' : 'details',
      masterpiece ? t('You used overlapping, a cast shadow and details. Which part do you love most?', 'Con đã dùng chồng lớp, bóng đổ và chi tiết. Con thích phần nào nhất?') : t('What is in front? What is behind?', 'Cái gì ở phía trước? Cái gì ở phía sau?'),
      masterpiece ? 'masterpiece' : 'key',
      masterpiece ? `golden-${plan.id}` : undefined,
    ),
  );
  if (masterpiece) items.push(storyBeat(id('finale'), plan, 4, 'finale'));
  return { id: lessonMiniUnitId(d.id), title: d.title, drawingId: d.id, skillXp: xpOf(d.skills), items, noDrawingFallback: 'completionAnimation' };
}

function thenNowUnit(d: Drawing, level: number): MiniUnit {
  const id = (s: string) => `${d.id}:${s}`;
  const src = d.thenNowOf ?? d.id;
  const items: MiniUnitItem[] = [
    { id: id('before'), type: 'thenNowCompare', sourceDrawingId: src, drawingId: d.id, phase: 'before', text: t('Back then, you drew this!', 'Hồi đó con vẽ thế này!') },
    { id: id('video'), type: 'video', drawingId: d.id, scaffold: d.scaffold ?? scaffoldForLevel(level) },
    { id: id('canvas'), type: 'canvas', drawingId: d.id, text: FINISH },
    { id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'details' },
    { id: id('after'), type: 'thenNowCompare', sourceDrawingId: src, drawingId: d.id, phase: 'after', text: t('Then and now: look how far you have come!', 'Ngày ấy và bây giờ: nhìn con tiến bộ thế nào!') },
    { id: id('journal'), type: 'uploadToJournal' },
    { id: id('celebrate'), type: 'celebration' },
  ];
  return { id: lessonMiniUnitId(d.id), title: d.title, drawingId: d.id, skillXp: xpOf(d.skills), items, noDrawingFallback: 'completionAnimation' };
}

function challengeUnit(c: ChallengeDef, byId: Map<string, Drawing>): MiniUnit {
  const id = (s: string) => `${c.id}:${s}`;
  const pool = c.pool.filter((p) => byId.has(p));
  const first = byId.get(pool[0]!)!;
  const peek = id('peek');
  const byDrawing = Object.fromEntries(pool.map((p) => [p, c.checklist?.[p] ?? checklistFor(byId.get(p)!)]));
  const seconds = c.peekSeconds ?? 0;
  const text =
    c.mode === 'noGuides'
      ? t('Pick one. The picture stays, but no steps and no helper lines: you can do it!', 'Chọn một bài. Tranh mẫu vẫn hiện, nhưng không có bước và nét phụ: con làm được!')
      : tf('Pick one, look closely for {n} seconds… then draw it from memory!', 'Chọn một bài, nhìn thật kỹ {n} giây… rồi vẽ lại theo trí nhớ!', { n: seconds });
  const items: MiniUnitItem[] = [
    { id: peek, type: 'memoryPeek', challengeId: c.id, drawingIds: pool, mode: c.mode, seconds, peeks: c.peeks, text },
    { id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'placement' },
    { id: id('check'), type: 'selfChecklist', challengeId: c.id, drawingId: first.id, items: byDrawing[first.id]!, byDrawing, fromChoice: peek, text: t('Check your drawing: did you remember…', 'Kiểm tra tranh của con: con có nhớ…') },
    ...tail(id, 'details', t('How did it feel to draw it on your own?', 'Tự vẽ một mình con thấy thế nào?'), 'badge'),
  ];
  return { id: `mu-${c.id}`, title: CHALLENGE_TITLES[c.level], drawingId: first.id, skillXp: xpOf(['details', 'shapes']), items, noDrawingFallback: 'completionAnimation' };
}

function observeUnit(o: ObserveDef, fallback: Drawing): MiniUnit {
  const id = (s: string) => `${o.id}:${s}`;
  const ref = o.refDrawingId ?? fallback.id;
  const items: MiniUnitItem[] = [{ id: id('guide'), type: 'observeGuide', observeId: o.id }];
  items.push({ id: id('canvas'), type: 'canvas', drawingId: ref, text: t("Here is Juno's drawing — yours will be different, and that's the fun!", 'Đây là tranh của cô Juno — tranh của con sẽ khác, vậy mới hay!') });
  items.push({ id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'details' });
  items.push({ id: id('compare'), type: 'observeCompare', observeId: o.id, text: t('Your object and your drawing, side by side', 'Đồ vật và tranh của con, đặt cạnh nhau') });
  items.push(...tail(id, 'details', t('What did you notice that you never saw before?', 'Con thấy điều gì mà trước đây chưa để ý?')));
  return { id: `mu-${o.id}`, title: o.title, drawingId: ref, skillXp: xpOf(['details']), items, noDrawingFallback: 'completionAnimation' };
}

function crossoverUnit(c: CrossoverDef, d: Drawing, plans: Map<PathId, PathPlanV2>): MiniUnit {
  const id = (s: string) => `${d.id}:${s}`;
  const [a, b] = c.worlds;
  const pa = plans.get(a)!;
  const pb = plans.get(b)!;
  const items: MiniUnitItem[] = [
    {
      id: id('story'),
      type: 'storyBeat',
      world: a,
      chapter: 5,
      speaker: pa.story.guide,
      title: d.title,
      line: tf('Two worlds meet! {a} and {b} made something new together.', 'Hai thế giới gặp nhau! {a} và {b} cùng tạo nên điều mới.', { a: pa.title, b: pb.title }),
      phase: 'intro',
    },
    { id: id('video'), type: 'video', drawingId: d.id, scaffold: d.scaffold ?? 'mainShapes' },
    { id: id('canvas'), type: 'canvas', drawingId: d.id, text: FINISH },
    { id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'details' },
    ...tail(id, 'creativity', t('Which world does your drawing remind you of more?', 'Tranh của con giống thế giới nào hơn?')),
  ];
  return { id: lessonMiniUnitId(d.id), title: d.title, drawingId: d.id, skillXp: xpOf([...new Set<SkillId>(['creativity', ...d.skills])]), items, noDrawingFallback: 'completionAnimation' };
}

const LIGHT_INTRO: Record<PictureLightKind, L> = {
  sun: t('This picture has one light: the *sun*', 'Bức tranh này có một nguồn sáng: *mặt trời*'),
  moon: t('This picture has one light: the *moon*', 'Bức tranh này có một nguồn sáng: *mặt trăng*'),
  window: t('This picture has one light: the *window*', 'Bức tranh này có một nguồn sáng: *cửa sổ*'),
  lamp: t('This picture has one light: a *lamp*', 'Bức tranh này có một nguồn sáng: *ngọn đèn*'),
};
/** The picture's light demo: source, highlight, shading side and cast shadow all follow the picture's own light. */
function pictureLightItem(itemId: string, d: Drawing): MiniUnitItem {
  const light = d.picture?.light;
  if (!light) return { id: itemId, type: 'animation', anim: 'lightSource', text: t('A picture has one light: everything is brighter on that side, and shadows fall the other way', 'Một bức tranh có một nguồn sáng: phía đó sáng hơn, bóng đổ về phía ngược lại') };
  const a = LIGHT_INTRO[light.kind];
  const where = lightPlace(light);
  const fall = shadowSide(light);
  return {
    id: itemId,
    type: 'animation',
    anim: 'lightSource',
    light,
    text: tf('{light}, {place}. The side facing it is bright, the other side is in shade, and shadows fall to the {side}', '{light}, {place}. Phía hướng về nó thì sáng, phía kia trong bóng râm, và bóng đổ về {side}', { light: a, place: where, side: fall }),
  };
}

/** Picture tier (Tier 2): object recap → composition → scenery → sky / ground colour → light → sign. */
function pictureUnit(d: Drawing, level: number): MiniUnit {
  const id = (s: string) => `${d.id}:${s}`;
  const lv = Math.min(4, Math.max(1, level)) as 1 | 2 | 3 | 4;
  const items: MiniUnitItem[] = [
    pictureLightItem(id('light'), d),
    { id: id('video'), type: 'video', drawingId: d.id, scaffold: d.scaffold ?? scaffoldForLevel(level) },
    { id: id('canvas'), type: 'canvas', drawingId: d.id, text: t('Finish your picture: the object, the background, then the colours', 'Hoàn thành bức tranh: vẽ vật, vẽ nền, rồi tô màu') },
    { id: id('coach'), type: 'colourCoach', drawingId: d.id, level: Math.max(2, lv) as 2 | 3 | 4 },
    { id: id('scan'), type: 'scanImage', allowSkipCapture: true, feedbackType: 'colour', drawingId: d.id },
    ...tail(id, 'shading', t('Your first real picture! Which part of the background do you like most?', 'Một bức tranh thật sự! Con thích phần nào của khung cảnh nhất?'), 'key', d.picture ? `golden-${d.picture.world}` : undefined),
  ];
  return { id: lessonMiniUnitId(d.id), title: d.title, drawingId: d.id, skillXp: xpOf(d.skills), items, noDrawingFallback: 'completionAnimation' };
}

/** Picture drawings of a world (Tier 2). */
export const worldPictures = (w: WorldV2): Drawing[] => w.drawings.filter((d) => d.activity === 'picture' && !!d.picture);

/** The world's first picture in map order (the Intro one is the free preview of the picture tier). */
export function firstPicture(w: WorldV2): string | undefined {
  const pics = worldPictures(w);
  for (const lv of w.plan.levels)
    for (const a of lv.activities) {
      const ids = a.type === 'core' ? [a.drawingId] : a.type === 'remix' ? (a.drawingIds ?? []) : [];
      for (const oid of ids) {
        const pd = pics.find((x) => x.picture!.of === oid && (x.picture!.activity ?? a.id) === a.id);
        if (pd) return pd.id;
      }
    }
  return undefined;
}

/* ---------------------------------------- paths ---------------------------------------- */

export function buildCurriculumV2(worlds: WorldV2Input[], byId: Map<string, Drawing>, startN = 0): BuiltCurriculumV2 {
  const miniUnits: MiniUnit[] = [];
  const paths: PathConfig[] = [];
  const challenges: ChallengeDef[] = [];
  const observe: ObserveDef[] = [];
  const crossovers: CrossoverDef[] = [];
  const plans = new Map(worlds.map((w) => [w.id, w.v2.plan] as const));
  let n = startN;
  for (const w of worlds) {
    const plan = w.v2.plan;
    const obsById = new Map(w.v2.observe.map((o) => [o.id, o] as const));
    const units: PathUnit[] = [];
    let k = 0;
    const pics = worldPictures(w.v2);
    const placedPics = new Set<string>();
    let freePicture = plan.id === 'intro';
    const pathDrawings = plan.levels.flatMap((l) => l.activities.flatMap((a) => (a.drawingId ? [byId.get(a.drawingId)] : []))).filter((d): d is Drawing => !!d);
    plan.levels.forEach((lv, li) => {
      const level = li + 1;
      const items: PathUnitItem[] = [{ id: `${plan.id}-l${level}-header`, type: 'header', title: lv.title }];
      lv.activities.forEach((a, ai) => {
        let mu: MiniUnit | undefined;
        let drawingId: string | undefined;
        if (a.type === 'remix') {
          const cards = (a.drawingIds ?? []).map((id) => byId.get(id)).filter((d): d is Drawing => !!d);
          if (cards.length) mu = remixUnit(a, cards, level);
          drawingId = cards[0]?.id;
        } else if (a.type === 'colouring') {
          const d = a.colourOf ? byId.get(a.colourOf) : undefined;
          if (d) mu = colouringUnit({ ...a, coinPiece: a.coinPiece ?? COIN_BY_ID[a.id] }, d, level);
          drawingId = d?.id;
        } else {
          const d = a.drawingId ? byId.get(a.drawingId) : undefined;
          if (d) {
            drawingId = d.id;
            if (a.type === 'doodle') mu = doodleUnit(d, level);
            else if (a.type === 'core') mu = coreUnit(d, level, n++, pathDrawings, plan.id === 'intro' && li === 0 && ai <= 1 && k === 1);
            else if (a.type === 'scene') mu = sceneUnit(d, plan, level, false);
            else if (a.type === 'masterpiece') mu = sceneUnit(d, plan, level, true);
            else if (a.type === 'thenNow') mu = thenNowUnit(d, level);
          }
        }
        if (!mu) return;
        // The level's story chapter opens on its first activity (storyBeat is skipped when stories are off).
        if (ai === 0) mu = { ...mu, items: [storyBeat(`${mu.id}:chapter`, plan, level, 'intro'), ...mu.items] };
        miniUnits.push(mu);
        const coin = a.coinPiece ?? (plan.id === 'intro' ? COIN_BY_ID[a.id] : undefined);
        const title = a.title ?? (drawingId ? byId.get(drawingId)?.title : undefined);
        items.push({
          id: `${plan.id}-l${level}-a${ai + 1}`,
          type: k === 0 ? 'container' : 'circular',
          miniUnitId: mu.id,
          ...(title ? { title } : {}),
          free: plan.id === 'intro' || k < 2 || !!a.free,
          activity: a.type,
          ...(a.gate === 'optional' ? { gate: 'optional' as const } : {}),
          ...(drawingId ? { drawingId } : {}),
          ...(coin ? { coinPiece: coin } : {}),
        });
        // Picture tier: one picture node per core lesson / remix card, hanging from the object node (never blocks).
        if (a.type === 'core' || a.type === 'remix') {
          const objIds = a.type === 'core' ? [a.drawingId] : (a.drawingIds ?? []);
          const nodeId = `${plan.id}-l${level}-a${ai + 1}`;
          objIds.forEach((oid) => {
            const pd = pics.find((x) => x.picture!.of === oid && (x.picture!.activity ?? a.id) === a.id);
            if (!pd) return;
            placedPics.add(pd.id);
            const pmu = pictureUnit(pd, level);
            miniUnits.push(pmu);
            items.push({ id: `${nodeId}-picture-${objIds.indexOf(oid) + 1}`, type: 'circular', miniUnitId: pmu.id, title: pd.title, free: freePicture, activity: 'picture', gate: 'optional', branch: 'picture', pictureOf: nodeId, drawingId: pd.id });
            freePicture = false;
          });
        }
        k++;
      });
      // Side branches: the level challenge and the observation prompts (never block).
      const c = lv.challenge;
      if (c.pool.some((p) => byId.has(p))) {
        challenges.push(c);
        const mu = challengeUnit(c, byId);
        miniUnits.push(mu);
        items.push({ id: `${plan.id}-l${level}-challenge`, type: 'circular', miniUnitId: mu.id, title: CHALLENGE_TITLES[c.level], free: !!c.free, activity: 'challenge', gate: 'optional', branch: 'challenge', drawingId: mu.drawingId });
      }
      (lv.observe ?? []).forEach((oid, oi) => {
        const o = obsById.get(oid);
        if (!o) return;
        observe.push(o);
        const mu = observeUnit(o, pathDrawings[0]!);
        miniUnits.push(mu);
        items.push({ id: `${plan.id}-l${level}-observe-${oi + 1}`, type: 'circular', miniUnitId: mu.id, title: o.title, free: !!o.free, activity: 'observe', gate: 'optional', branch: 'observe', drawingId: mu.drawingId });
      });
      if (level < 4) items.push({ id: `${plan.id}-l${level}-key`, type: 'key' });
      else {
        items.push({ id: `${plan.id}-finale`, type: 'finale', title: plan.story.title });
        // Picture tier: the world's exhibition room (opens when every picture of the world is done).
        if (placedPics.size) items.push({ id: `${plan.id}-gallery`, type: 'gallery', title: t('Exhibition room', 'Phòng triển lãm') });
      }
      units.push({ id: `${plan.id}-l${level}`, level, title: lv.title, items, chapter: lv.chapter });
    });
    // Observation prompts not placed on the map still get a mini unit (playground shelf).
    for (const o of w.v2.observe) if (!observe.includes(o)) {
      observe.push(o);
      miniUnits.push(observeUnit(o, pathDrawings[0]!));
    }
    // Pictures of playground-only items (not on the map) still get their lesson.
    for (const pd of pics) if (!placedPics.has(pd.id)) miniUnits.push(pictureUnit(pd, 2));
    const hero = [...plan.levels[3].activities].reverse().find((a) => a.type === 'core' && a.drawingId && byId.has(a.drawingId))?.drawingId ?? pathDrawings[0]?.id ?? '';
    paths.push({ id: plan.id, title: plan.title, description: plan.description, color: plan.color, heroDrawingId: hero, units, suggestNext: plan.suggestNext, story: plan.story });
    for (const c of w.v2.crossovers) {
      const d = byId.get(c.drawingId);
      if (!d) continue;
      crossovers.push(c);
      miniUnits.push(crossoverUnit(c, d, plans));
    }
  }
  const { playground, miniUnits: pgUnits } = buildPlaygroundV2(worlds, byId, n);
  return { paths, miniUnits: [...miniUnits, ...pgUnits], playground, levelChallenges: challenges, observe, crossovers };
}

/* -------------------------------------- playground -------------------------------------- */

/**
 * 4-level playground (docs §D): quick doodles (level of their path slot), the world's own category for
 * cores / every remix card / then & now / playground-only items, crossovers at level 4 (label + lock
 * until both worlds are complete). Every v1 `pg-<id>` keeps existing.
 */
export function buildPlaygroundV2(worlds: WorldV2Input[], byId: Map<string, Drawing>, startN = 0): { playground: PlaygroundConfig; miniUnits: MiniUnit[] } {
  const items: PlaygroundItem[] = [];
  const miniUnits: MiniUnit[] = [];
  const seen = new Set<string>();
  let n = startN;
  const add = (d: Drawing, categoryId: string, level: number, extra: Partial<PlaygroundItem> = {}) => {
    if (seen.has(d.id)) return;
    seen.add(d.id);
    const mu = buildPlaygroundMiniUnit(d, n++);
    miniUnits.push(mu);
    const season = SEASONS_V2[d.id] ?? (d.id.startsWith('nature-remix-pine-') ? 'winter' : d.id.startsWith('cute-remix-gift-box-') ? 'holiday' : undefined);
    items.push({
      id: playgroundItemId(d.id),
      categoryId: d.playgroundCategory ?? categoryId,
      miniUnitId: mu.id,
      drawingId: d.id,
      level: Math.min(4, Math.max(1, level)) as 1 | 2 | 3 | 4,
      tags: [d.category, d.subject, ...d.skills],
      ...(season ? { season } : {}),
      ...(PREVIEW_DRAWINGS.includes(d.id) ? { free: true } : {}),
      ...extra,
    });
  };
  for (const w of worlds) {
    const cat = WORLD_PG_CATEGORY[w.id];
    w.v2.plan.levels.forEach((lv, li) => {
      for (const a of lv.activities) {
        const d = a.drawingId ? byId.get(a.drawingId) : undefined;
        if (a.type === 'doodle' && d) add(d, 'quick', li + 1);
        else if ((a.type === 'core' || a.type === 'thenNow') && d) add(d, cat, li + 1);
        else if (a.type === 'remix') for (const id of a.drawingIds ?? []) {
          const c = byId.get(id);
          if (c) add(c, cat, li + 1, { remixOf: a.id });
        }
      }
    });
    for (const id of w.v2.playgroundOnly) {
      const d = byId.get(id);
      if (d) add(d, cat, 1);
    }
  }
  for (const w of worlds) for (const c of w.v2.crossovers) {
    const d = byId.get(c.drawingId);
    if (d) add(d, WORLD_PG_CATEGORY[c.worlds[0]], 4, { crossover: c.worlds });
  }
  // Picture tier shelf: every picture (same mini unit as its map node), at its object's level.
  for (const w of worlds) {
    const lvOf = new Map(w.v2.plan.levels.flatMap((lv, li) => lv.activities.map((a) => [a.id, li + 1] as const)));
    const free = w.id === 'intro' ? firstPicture(w.v2) : undefined;
    for (const d of worldPictures(w.v2)) {
      const pic = d.picture!;
      items.push({
        id: playgroundItemId(d.id),
        categoryId: PICTURES_CATEGORY,
        miniUnitId: lessonMiniUnitId(d.id),
        drawingId: d.id,
        level: Math.min(4, Math.max(1, (pic.activity && lvOf.get(pic.activity)) || 2)) as 1 | 2 | 3 | 4,
        picture: pic.of,
        tags: [d.category, d.subject, 'picture'],
        ...(d.id === free ? { free: true } : {}),
      });
    }
  }
  return { playground: { categories: PLAYGROUND_CATEGORIES_V2, items, previewItemIds: PREVIEW_DRAWINGS.map(playgroundItemId).filter((id) => items.some((i) => i.id === id)) }, miniUnits };
}

/** Add v1 drawings the v2 plans do not place (keeps every v1 `pg-<id>`), at their v1 band. */
export function keepV1Playground(pg: { playground: PlaygroundConfig; miniUnits: MiniUnit[] }, v1Items: PlaygroundItem[], byId: Map<string, Drawing>, n: number) {
  const have = new Set(pg.playground.items.map((i) => i.id));
  for (const it of v1Items) {
    if (have.has(it.id)) continue;
    const d = byId.get(it.drawingId);
    if (!d) continue;
    pg.miniUnits.push(buildPlaygroundMiniUnit(d, n++));
    pg.playground.items.push({ ...it, categoryId: it.categoryId === 'quick' ? 'quick' : it.categoryId });
  }
  return pg;
}

/* --------------------------------------- lab --------------------------------------- */

/** Debug "v2 lab" (cheats + e2e/curriculum-v2.mjs): one mini unit with each of the nine v2 item types. */
export function buildV2LabMiniUnit(built: BuiltCurriculumV2, byId: Map<string, Drawing>): MiniUnit | null {
  const units = new Map(built.miniUnits.map((m) => [m.id, m] as const));
  const find = <T extends MiniUnitItem['type']>(type: T) => {
    for (const m of built.miniUnits) for (const it of m.items) if (it.type === type) return it as Extract<MiniUnitItem, { type: T }>;
    return undefined;
  };
  const types = ['storyBeat', 'remixPicker', 'sceneStage', 'memoryPeek', 'selfChecklist', 'observeGuide', 'observeCompare', 'thenNowCompare', 'printSheet'] as const;
  const items: MiniUnitItem[] = [];
  for (const ty of types) {
    if (ty === 'thenNowCompare') {
      const b = find('thenNowCompare');
      if (b) {
        items.push({ ...b, id: 'lab2:thenNow-before', phase: 'before' });
        items.push({ ...b, id: 'lab2:thenNow-after', phase: 'after' });
      }
      continue;
    }
    const it = find(ty);
    if (it) items.push({ ...it, id: `lab2:${ty}` } as MiniUnitItem);
  }
  // Re-point choice links to the lab ids.
  for (const it of items) {
    if ((it.type === 'selfChecklist') && it.fromChoice) (it as { fromChoice?: string }).fromChoice = 'lab2:memoryPeek';
  }
  const first = byId.values().next().value as Drawing | undefined;
  if (!items.length || !first) return null;
  void units;
  return { id: 'mu-lab-v2', title: t('Curriculum v2 lab', 'Phòng thử lộ trình v2'), drawingId: first.id, skillXp: {}, items, noDrawingFallback: 'completionAnimation' };
}
